import React from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Alert from '@amzn/meridian/alert';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Card from '@amzn/meridian/card';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Loader from '@amzn/meridian/loader';
import Input from '@amzn/meridian/input';
import { getDateInUTC } from '../utils/GetDateUtils';
import { SwitchButtonValue, ZoneCreationProperties } from '../types/zoneMapType';
import { FixtureAssignment } from '../allocation/computeFixtureAllocations';

interface ZoneViewerModalsProps {
  openConfirmationModal: boolean;
  setOpenConfirmationModal: (value: boolean) => void;
  publishZoneErrorMsg: string | undefined;
  setPublishZoneErrorMsg: (value: string | undefined) => void;
  publishZoneSuccessful: boolean;
  setPublishZoneSuccessful: (value: boolean) => void;
  onPublishButton: () => Promise<void>;
  store: string;
  timeStamp: number;
  zonesToUpdate: Map<number, ZoneCreationProperties>;
  isLoading: boolean;
  onClosingSuccessfulModal: () => void;
  copyToNewTimestamp: number;
  switchButtonValue: SwitchButtonValue;
  fixtureAssignments?: FixtureAssignment[];
  macroplanogramNameInput?: string;
  onMacroplanogramNameChange?: (value: string) => void;
}

const ZoneViewerModalComponent = (props: ZoneViewerModalsProps) => {
  const {
    openConfirmationModal,
    setOpenConfirmationModal,
    publishZoneErrorMsg,
    setPublishZoneErrorMsg,
    publishZoneSuccessful,
    setPublishZoneSuccessful,
    onPublishButton,
    store,
    timeStamp,
    zonesToUpdate,
    isLoading,
    onClosingSuccessfulModal,
    copyToNewTimestamp,
    switchButtonValue,
    fixtureAssignments,
    macroplanogramNameInput,
    onMacroplanogramNameChange,
  } = props;

  const publishZoneDate = getDateInUTC(
    switchButtonValue === SwitchButtonValue.CopyToNewMap ? copyToNewTimestamp : timeStamp,
  );

  const hasMacroplanogramName = (macroplanogramNameInput ?? '').trim().length > 0;

  // Build subcategory → fixture IDs map from assignments
  const subcategoryFixtureMap = new Map<string, string[]>();
  if (fixtureAssignments) {
    for (const assignment of fixtureAssignments) {
      if (!subcategoryFixtureMap.has(assignment.subcategoryId)) {
        subcategoryFixtureMap.set(assignment.subcategoryId, []);
      }
      subcategoryFixtureMap.get(assignment.subcategoryId)!.push(assignment.fixtureId);
    }
  }

  const onCancelPublishButton = () => {
    setOpenConfirmationModal(false);
  };

  return (
    <>
      <Modal
        open={openConfirmationModal}
        scrollContainer="viewport"
        width="1100px"
        aria-describedby="modal-description"
      >
        {isLoading ? (
          <div className="loader-container">
            <Loader type="circular" size="large" />
          </div>
        ) : (
          !publishZoneErrorMsg &&
          !publishZoneSuccessful && (
            <>
              {onMacroplanogramNameChange && (
                <Row spacingInset="300" alignmentHorizontal="center">
                  <Input
                    label="Macroplanogram Name"
                    value={macroplanogramNameInput ?? ''}
                    onChange={onMacroplanogramNameChange}
                    placeholder="e.g., Ambient Produce"
                    width={400}
                  />
                </Row>
              )}
              <Row alignmentHorizontal="center" spacingInset="300" spacing="400">
                <Card>
                  <Table headerRows={1} spacing="small">
                    <TableRow>
                      <TableCell alignmentHorizontal="center">StoreId</TableCell>
                      <TableCell alignmentHorizontal="center">Live Date</TableCell>
                      <TableCell alignmentHorizontal="center">Subcategory</TableCell>
                      <TableCell alignmentHorizontal="center">Allocation %</TableCell>
                      <TableCell alignmentHorizontal="center">Fixture Count</TableCell>
                      <TableCell alignmentHorizontal="center">Fixture IDs</TableCell>
                    </TableRow>
                    {Array.from(zonesToUpdate.values()).flatMap((value) => {
                      const allocations = (value.allocations ?? []).filter((a) => a.subcategoryId && a.percentage > 0);
                      if (allocations.length > 0) {
                        return allocations.map((alloc, idx) => {
                          const fixtures = subcategoryFixtureMap.get(alloc.subcategoryId) ?? [];
                          return (
                            <TableRow key={`${value.categoryId}-${alloc.subcategoryId}`}>
                              {idx === 0 && (
                                <>
                                  <TableCell alignmentHorizontal="center" rowSpan={allocations.length}>
                                    {store}
                                  </TableCell>
                                  <TableCell alignmentHorizontal="center" rowSpan={allocations.length}>
                                    <Text>{publishZoneDate}</Text>
                                  </TableCell>
                                </>
                              )}
                              <TableCell alignmentHorizontal="center">{alloc.subcategoryName}</TableCell>
                              <TableCell alignmentHorizontal="center">{alloc.percentage}%</TableCell>
                              <TableCell alignmentHorizontal="center">{fixtures.length}</TableCell>
                              <TableCell alignmentHorizontal="center">
                                <div style={{ maxHeight: '120px', overflowY: 'auto', fontSize: '12px' }}>
                                  {fixtures.map((fId) => (
                                    <div key={fId}>{fId}</div>
                                  ))}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        });
                      }
                      return [
                        <TableRow key={value.categoryId}>
                          <TableCell alignmentHorizontal="center">{store}</TableCell>
                          <TableCell alignmentHorizontal="center">
                            <Text>{publishZoneDate}</Text>
                          </TableCell>
                          <TableCell alignmentHorizontal="center">
                            {value.subcategoryNames?.map((subCategory: string) => (
                              <div key={subCategory}>{subCategory}</div>
                            ))}
                          </TableCell>
                          <TableCell alignmentHorizontal="center">-</TableCell>
                          <TableCell alignmentHorizontal="center">{value.fixtureIds?.length ?? 0}</TableCell>
                          <TableCell alignmentHorizontal="center">
                            <div style={{ maxHeight: '120px', overflowY: 'auto', fontSize: '12px' }}>
                              {value.fixtureIds?.map((fId) => (
                                <div key={fId}>{fId}</div>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>,
                      ];
                    })}
                  </Table>
                </Card>
              </Row>
              <ModalFooter>
                <Row alignmentHorizontal="center" widths="fit">
                  <Button
                    type="secondary"
                    size="large"
                    data-testid="CancelPublishButton"
                    onClick={onCancelPublishButton}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    data-testid="PublishButton"
                    onClick={onPublishButton}
                    disabled={!hasMacroplanogramName}
                  >
                    Confirm
                  </Button>
                </Row>
              </ModalFooter>
            </>
          )
        )}
      </Modal>

      {publishZoneErrorMsg && (
        <Modal
          open
          scrollContainer="viewport"
          aria-describedby="modal-description"
          onClose={() => {
            setPublishZoneErrorMsg(undefined);
          }}
        >
          <Alert size="xlarge" type="error">
            <h2>Error Message!</h2>
          </Alert>
          <Text>{publishZoneErrorMsg}</Text>
          <Row alignmentHorizontal="center" widths="fit" />
        </Modal>
      )}

      {publishZoneSuccessful && (
        <Modal
          open={publishZoneSuccessful}
          scrollContainer="viewport"
          aria-describedby="modal-description"
          onClose={() => {
            setPublishZoneErrorMsg(undefined);
            setPublishZoneSuccessful(false);
            onClosingSuccessfulModal();
          }}
        >
          <Alert type="success" size="xlarge">
            Zone Map Published Successfully
          </Alert>
        </Modal>
      )}
    </>
  );
};

export default ZoneViewerModalComponent;
